import type { ICaveat } from './types.js';

/**
 * Authored data-quality caveats that consumers should know about when reading
 * a number derived from these canonical definitions.
 *
 * The first two caveats reference findings from US1 (PRODOPSAI-411). Replace the
 * placeholder text with the verbatim findings once US1’s `findings.md` lands.
 */
export const CAVEATS: ReadonlyArray<ICaveat> = [
  {
    caveatKey: 'status-normalized-stale-on-sync',
    title: '`status_normalized` may be stale relative to `issue_status`',
    description: [
      'Multiple rows have been observed where `issue_status = "Done"` but `status_normalized = "In Progress"` (or vice versa).',
      'Root cause is owned by US1 (PRODOPSAI-411) — the bug is in either Fabric (source emits stale value) or in Skystone’s sync (we don’t recompute on update).',
      'Until that lands, any metric that filters on `status_normalized` (Open, Cancelled) may include rows that have transitioned in the source but not yet in the mirror.',
    ].join('\n'),
    affectedConcepts: ['open', 'cancelled'],
  },
  {
    caveatKey: 'days-in-progress-null-semantic',
    title: '`days_in_progress` is NULL for ~97% of currently-in-progress issues',
    description: [
      'The `days_in_progress` column was assumed to mean "age while in flight" but is in fact populated only on completion (cycle-time-on-completion).',
      'For ~97% of in-progress rows the column is NULL. Use `first_in_progress_date` for age instead.',
      'US1 (PRODOPSAI-411) confirms the semantic with the Fabric team and adds `COMMENT ON COLUMN` to capture the answer in the schema.',
    ].join('\n'),
    affectedConcepts: ['wip-avg-age', 'wip-aging'],
  },
  {
    caveatKey: 'subtask-spelling-variants',
    title: 'Sub-task and Subtask appear with both spellings',
    description: [
      'The source data contains both `Sub-task` and `Subtask` for the same issue type.',
      'The canonical issue-type whitelist (`Story / Bug / Task`) excludes both, so this does not affect headline counts.',
      'Be aware when writing new ad-hoc queries: matching only `Sub-task` will let `Subtask` rows through.',
    ].join('\n'),
    affectedConcepts: ['wip-current', 'wip-trend', 'wip-avg-age', 'wip-aging', 'open', 'total', 'cancelled', 'throughput'],
  },
  {
    caveatKey: 'sonar-project-bridge-required',
    title: 'Repositories without a SonarCloud project bridge are invisible to every code-quality calculation',
    description: [
      'Every code-quality concept on the Code Quality tab inner-joins `raw_dim_sonarqube_project` on `repository_id`. A non-archived repository that does **not** have a row in `raw_dim_sonarqube_project` is silently excluded from every calculation — verdict-strip tiles, trend sparklines, distribution panels, team breakouts, and the freshness footer all skip it.',
      "When a repository name does not match between SonarCloud and Skystone the row never lands in `raw_dim_sonarqube_project`. The Caveat banner above the tab surfaces the most recent sync run's match counters (matched / unmatched / ambiguous) so the cohort gap is at least visible.",
      'When an admin says "this repo is missing from the tab", the first thing to check is whether it has a SonarCloud project row.',
    ].join('\n'),
    affectedConcepts: [
      'cq-coverage-current',
      'cq-coverage-trend',
      'cq-qg-pass-rate',
      'cq-reliability-current',
      'cq-reliability-trend',
      'cq-maintainability-trend',
      'cq-by-team',
      'cq-failing-qg-list',
      'cq-lowest-coverage-list',
      'cq-worst-reliability-list',
      'cq-freshness',
      'cq-sync-caveats',
    ],
  },
  {
    caveatKey: 'coverage-locf-no-time-cap',
    title: 'Coverage carries forward indefinitely — long-dormant repositories still count',
    description: [
      'Coverage Trend uses last-observation-carried-forward (LOCF) for each repository, with no time cap (PRODOPSAI-440 product call). A repository whose latest coverage scan was months or years ago continues to contribute that value to every weekly bucket until a newer scan lands.',
      'The Median Coverage tile (snapshot) has the same effect by construction: it reads the latest scan per repository regardless of how old that scan is.',
      "Effect on admin questions: 'why is portfolio coverage still 78% when nobody has scanned this team's repos in months?' — answer: those repos' last-known coverage is still being counted. The Freshness footer reports the cohort-wide newest scan date so the admin can quantify how stale the cohort is overall.",
    ].join('\n'),
    affectedConcepts: [
      'cq-coverage-current',
      'cq-coverage-trend',
      'cq-by-team',
      'cq-lowest-coverage-list',
      'cq-sync-caveats',
    ],
  },
] as const;
